/**
 * InsightsAccessContext — single source of truth for whether the
 * current user is in the INSIGHTS_ADMINS allowlist.
 *
 * Hits /api/intelligence/access once per session and caches the result.
 * Used by the navbar (hide link) and the route guard (redirect).
 *
 * Race-condition note: <TokenSync> writes the Auth0 access token to
 * localStorage asynchronously after isAuthenticated flips true. The
 * shared `api` interceptor reads that token at request time — if we
 * fire the access check before the token lands, the request goes out
 * with no Authorization header → 401. We therefore wait for the token
 * (polling localStorage briefly) before issuing the check.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../services/api';
import { useAuth0 } from './AuthContext';

const InsightsAccessContext = createContext({ allowed: false, loading: true });

export const useInsightsAccess = () => useContext(InsightsAccessContext);

export const InsightsAccessProvider = ({ children }) => {
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      setAllowed(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const tryCheck = async (attempt = 0) => {
      if (cancelled) return;
      const token = localStorage.getItem('authToken');
      // Wait up to ~6s for TokenSync to drop the token in localStorage.
      // Dev mode never writes a token; fall through and let the backend
      // decide (the dev mock token isn't a valid JWT anyway).
      if (!token && attempt < 20) {
        setTimeout(() => tryCheck(attempt + 1), 300);
        return;
      }
      try {
        const r = await api.get('/intelligence/access', { timeout: 8000 });
        if (!cancelled) setAllowed(!!r.data?.allowed);
      } catch {
        if (!cancelled) setAllowed(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    tryCheck();
    return () => { cancelled = true; };
  }, [isAuthenticated, isLoading]);

  return (
    <InsightsAccessContext.Provider value={{ allowed, loading }}>
      {children}
    </InsightsAccessContext.Provider>
  );
};
